import { IonSpinner } from "@ionic/react";
import React, { type Dispatch, type SetStateAction } from "react";
import { ITINERARY_PANEL } from "../../../config/itineraryPanel.configuration";
import { type SavedItineraryEntry } from "../../../store/persistence/slices/savedItineraries.slice";
import { type SavedListFilterTabId } from "../../../tools/savedItineraryDisplay.tools";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import { ItineraryDebugListToolbar } from "../../Debug/itineraryDebug";
import { type PanelResizeLayout } from "../panelResize";
import MyCarProposition from "../pricing/MyCarProposition";
import SavedItinerariesEmptyState from "../savedItinerary/SavedItinerariesEmptyState";
import ItineraryItem from "./ItineraryItem";
import { SAVED_LIST_FILTER_TABS } from "./itineraryList.tools";
import ItineraryListChromeRow from "./ItineraryListChromeRow";
import ItineraryListFilterTabs from "./ItineraryListFilterTabs";
import NoResults from "./NoResults";
import SavedItineraryAccordionList from "./SavedItineraryAccordionList";

interface CarDetailsState {
    "tollPrice":  number;
    "distance":   number;
    "totalPrice": number;
}

interface ItineraryListPanelViewProps {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Whether prices display in EUR. */
    "isEuro": boolean;

    /** Shared resize layout measurements from Home. */
    "panelResizeLayout": PanelResizeLayout;

    /** List content mode (search results vs saved itineraries). */
    "itineraryListContentMode": ContainerProps["itineraryListContentMode"];

    /** Closes the list panel (saved mode or search results). */
    "onCloseList": () => void;

    /** Enables itinerary debug tooling in the list panel. */
    "debugItineraries": boolean;

    /** Whether list debug relaunch actions are disabled. */
    "isListDebugActionDisabled": boolean;

    /** Relaunches list segmentation for debug. */
    "onRelaunchListSegmentation": () => void;

    /** Active saved-list filter tab id. */
    "activeSavedFilterTab": SavedListFilterTabId;

    /** Updates the active saved-list filter tab. */
    "onActiveSavedFilterTabChange": Dispatch<SetStateAction<SavedListFilterTabId>>;

    /** Whether the panel shows the saved-itinerary list. */
    "isSavedListMode": boolean;

    /** Whether the saved list shell (accordion or exit presence) should render. */
    "shouldRenderSavedListShell": boolean;

    /** Past temporal bucket entries. */
    "pastDisplayedSavedEntries": SavedItineraryEntry[];

    /** Current temporal bucket entries. */
    "currentDisplayedSavedEntries": SavedItineraryEntry[];

    /** Upcoming temporal bucket entries. */
    "upcomingDisplayedSavedEntries": SavedItineraryEntry[];

    /** Empty-state variant when the saved shell is not rendered. */
    "savedEmptyStateVariant": "noSaved" | "noUpcoming" | "filteredEmpty";

    /** Live search itineraries (pre-filter / mode-aware displayed list). */
    "displayedItineraries": ItineraryDTO[];

    /** Raw search itineraries length used for the empty NoResults branch. */
    "itinerariesCount": number;

    /** Whether the main OD search loader is active. */
    "loading": boolean;

    /** Whether departure/arrival coordinates are usable for search. */
    "hasValidCoordinates": boolean;

    /** Selected departure/arrival inputs for NoResults / MyCar. */
    "selectedInputs": ContainerProps["selectedInputs"];

    /** Whether TravelProgress should animate its segment reveal. */
    "travelProgressRevealActive": boolean;

    /** Whether itinerary completion is still loading. */
    "loadingItinerary": boolean;

    /** Whether pricing evaluation is still loading. */
    "loadingPricing": boolean;

    /** Pricing preferences for row chrome. */
    "pricingPreferences": ItineraryPricingPreferencesDTO;

    /** Opens itinerary details for a row. */
    "onItineraryClick": ( itinerary: ItineraryDTO ) => void;

    /** Optional long-press handler (dev review modal). */
    "onItineraryLongPress"?: ( itinerary: ItineraryDTO ) => void;

    /** Removes one saved itinerary. */
    "onRemoveSavedItinerary": ( itineraryId: string ) => void;

    /** Called when saved-list presence exit finishes. */
    "onSavedListExitComplete": () => void;

    /** MYCAR proposition itinerary, when available. */
    "ITakeMyCarData": ContainerProps["ITakeMyCarData"];

    /** Wear cost per km used by the MyCar price pill. */
    "wearCostPerKmChf": number;

    /** Resets the panel to the expanded snap tier. */
    "onResetPanelExpanded": () => void;

    /** Whether the car price detail sheet is open. */
    "isCarPriceOpen": boolean;

    /** Opens/closes the car price detail sheet. */
    "setIsCarPriceOpen": Dispatch<SetStateAction<boolean>>;

    /** Computed car toll/distance/total details. */
    "carDetails": CarDetailsState | null;

    /** Updates car toll/distance/total details. */
    "setCarDetails": Dispatch<SetStateAction<CarDetailsState | null>>;

    /** Switches the panel into MyCar selection mode. */
    "setITakeMyCar": Dispatch<SetStateAction<boolean>>;
}

/**
 * Presentational itinerary list panel (chrome, filters, saved/search content, MyCar footer).
 *
 * @param props List panel composition props.
 * @returns List panel layout root.
 */
const ItineraryListPanelView: React.FC<ItineraryListPanelViewProps> = ( {
    isDarkUi,
    isEuro,
    panelResizeLayout,
    itineraryListContentMode,
    onCloseList,
    debugItineraries,
    isListDebugActionDisabled,
    onRelaunchListSegmentation,
    activeSavedFilterTab,
    onActiveSavedFilterTabChange,
    isSavedListMode,
    shouldRenderSavedListShell,
    pastDisplayedSavedEntries,
    currentDisplayedSavedEntries,
    upcomingDisplayedSavedEntries,
    savedEmptyStateVariant,
    displayedItineraries,
    itinerariesCount,
    loading,
    hasValidCoordinates,
    selectedInputs,
    travelProgressRevealActive,
    loadingItinerary,
    loadingPricing,
    pricingPreferences,
    onItineraryClick,
    onItineraryLongPress,
    onRemoveSavedItinerary,
    onSavedListExitComplete,
    ITakeMyCarData,
    wearCostPerKmChf,
    onResetPanelExpanded,
    isCarPriceOpen,
    setIsCarPriceOpen,
    carDetails,
    setCarDetails,
    setITakeMyCar,
} ) => {
    return (
        <div
            className="flex min-h-0 flex-1 flex-col"
            id="itinerary-list-root"
        >
            <ItineraryListChromeRow
                contentMode={itineraryListContentMode}
                isDarkUi={isDarkUi}
                onCloseList={onCloseList}
                panelResizeLayout={panelResizeLayout}
            />
            <div
                className="flex min-h-0 flex-1 flex-col p-[5px] pt-0"
                id="itinerary-list-content"
            >
                <ItineraryDebugListToolbar
                    debugItineraries={debugItineraries}
                    isDarkUi={isDarkUi}
                    isDisabled={isListDebugActionDisabled}
                    onRelaunch={onRelaunchListSegmentation}
                />
                {isSavedListMode && ITINERARY_PANEL.enableSavedItineraryListFilters
                    ? (
                        <ItineraryListFilterTabs
                            activeTabId={activeSavedFilterTab}
                            className="mb-2"
                            id="saved-list-filter-tabs"
                            isDarkUi={isDarkUi}
                            onActiveTabChange={onActiveSavedFilterTabChange}
                            tabs={SAVED_LIST_FILTER_TABS}
                        />
                    )
                    : null}
                {isSavedListMode
                    ? shouldRenderSavedListShell
                        ? (
                            <SavedItineraryAccordionList
                                currentEntries={currentDisplayedSavedEntries}
                                isDarkUi={isDarkUi}
                                isEuro={isEuro}
                                onItineraryClick={onItineraryClick}
                                onRemoveSavedItinerary={onRemoveSavedItinerary}
                                onSavedListExitComplete={onSavedListExitComplete}
                                pastEntries={pastDisplayedSavedEntries}
                                pricingPreferences={pricingPreferences}
                                upcomingEntries={upcomingDisplayedSavedEntries}
                            />
                        )
                        : (
                            <SavedItinerariesEmptyState
                                isDarkUi={isDarkUi}
                                variant={savedEmptyStateVariant}
                            />
                        )

                    : itinerariesCount === 0 && !loading && hasValidCoordinates
                        ? (
                            <NoResults
                                arrivalLat={selectedInputs.arrival.lat}
                                arrivalLon={selectedInputs.arrival.lon}
                                departureLat={selectedInputs.departure.lat}
                                departureLon={selectedInputs.departure.lon}
                            />
                        )
                        : (
                            <div
                                className="flowrideScrollBar min-h-0 flex-1 overflow-auto scroll-container [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                                id="itinerary-list-scroll"
                            >
                                <div
                                    className="flex flex-col gap-2"
                                    id="itinerary-list-items"
                                >
                                    {displayedItineraries.map( ( itinerary, index ) => (
                                        <ItineraryItem
                                            animateTravelProgress={travelProgressRevealActive}
                                            handleItineraryClick={onItineraryClick}
                                            index={index}
                                            isDarkUi={isDarkUi}
                                            isEuro={isEuro}
                                            isPriceLoading={loadingPricing}
                                            itinerary={itinerary}
                                            key={itinerary.id ?? index}
                                            onItineraryLongPress={onItineraryLongPress}
                                            pricingPreferences={pricingPreferences}
                                        />
                                    ) )}
                                    {loadingItinerary && (
                                        <div
                                            className="text-center bold flex justify-center h-10"
                                            id="itinerary-list-loading"
                                        >
                                            Chargement des itinéraires en cours&nbsp;
                                            <IonSpinner
                                                className="h-1 w-5 mt-[-2px]"
                                                color="primary"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                {!isSavedListMode && ITakeMyCarData != null && (
                    <MyCarProposition
                        ITakeMyCarData={ITakeMyCarData}
                        carDetails={carDetails}
                        isCarPriceOpen={isCarPriceOpen}
                        isDarkUi={isDarkUi}
                        isEuro={isEuro}
                        onResetPanelExpanded={onResetPanelExpanded}
                        selectedInputs={selectedInputs}
                        setCarDetails={setCarDetails}
                        setITakeMyCar={setITakeMyCar}
                        setIsCarPriceOpen={setIsCarPriceOpen}
                        wearCostPerKmChf={wearCostPerKmChf}
                    />
                )}
            </div>
        </div>
    );
};

export default ItineraryListPanelView;
